import type { AudienceDeletePreview, AudienceDisplayStatus, MetaAdSet } from '../types';
import { destinationKindLabel } from './adAccounts';

/** Why a definition in a given display status cannot be deleted yet, since proxima-audiences refuses the delete in both. */
export const DELETE_BLOCKED_REASONS = {
  Building: 'Wait for the run to finish before deleting this audience',
  Delivering: 'Wait for delivery to finish before deleting this audience'
} as const satisfies Partial<Record<AudienceDisplayStatus, string>>;

/** The reason the Delete button is disabled, or null when it is enabled. */
export const deleteBlockedReason = (status: AudienceDisplayStatus | null): string | null =>
  status === 'Building' || status === 'Delivering' ? DELETE_BLOCKED_REASONS[status] : null;

/** One Meta audience in the delete dialog. */
export type DeletePreviewAudience = {
  /** Unique within the preview. */
  key: string;
  /** "Seed", "LAL 10%", or "Lookalike" for one no destination tracks. */
  label: string;
  /** The name on Meta, or null when Meta has none or was not read. */
  name: string | null;
  /** False for a lookalike Proxima did not create, such as one built by hand in Ads Manager. */
  tracked: boolean;
  /** True for a lookalike, shown under its seed. */
  nested: boolean;
  /** True when nothing was created on Meta yet, so there is nothing to remove there. */
  pending: boolean;
  adSets: MetaAdSet[];
  /** Meta's message when its ad sets could not be read, else null. */
  adSetsError: string | null;
  adSetsUnknown: boolean;
};

/** One ad account's audiences in the delete dialog. */
export type DeletePreviewAccount = {
  adAccountId: string;
  adAccountName: string | null;
  isProximaOwned: boolean;
  /** The seed, then its lookalikes in preview order, then the lookalikes no destination tracks. */
  audiences: DeletePreviewAudience[];
};

/**
 * Groups a delete preview by ad account for display, each seed ahead of its lookalikes.
 *
 * Accounts keep the preview's order.
 * An untracked lookalike joins its seed's account.
 */
export const groupDeletePreview = (preview: AudienceDeletePreview): DeletePreviewAccount[] => {
  const accounts = new Map<string, DeletePreviewAccount>();
  const accountOfDestination = new Map<number, DeletePreviewAccount>();
  for (const destination of preview.destinations) {
    const account = accounts.get(destination.adAccountId) ?? {
      adAccountId: destination.adAccountId,
      adAccountName: destination.adAccountName,
      isProximaOwned: destination.isProximaOwned,
      audiences: []
    };
    accounts.set(destination.adAccountId, account);
    accountOfDestination.set(destination.destinationId, account);
    const audience: DeletePreviewAudience = {
      key: `destination:${destination.destinationId}`,
      label: destinationKindLabel({ ...destination, lookalikeCountry: null }),
      name: destination.externalName,
      tracked: true,
      nested: destination.kind === 'lookalike',
      pending: destination.status === 'pending',
      adSets: destination.adSets,
      adSetsError: destination.adSetsError,
      adSetsUnknown: destination.adSetsUnknown
    };
    account.audiences =
      destination.kind === 'seed'
        ? [audience, ...account.audiences]
        : [...account.audiences, audience];
  }
  for (const lookalike of preview.untrackedLookalikes) {
    const account = accountOfDestination.get(lookalike.seedDestinationId);
    account?.audiences.push({
      key: `untracked:${lookalike.id}`,
      label: 'Lookalike',
      name: lookalike.name ?? lookalike.id,
      tracked: false,
      nested: true,
      pending: false,
      adSets: lookalike.adSets,
      adSetsError: lookalike.adSetsError,
      adSetsUnknown: lookalike.adSetsUnknown
    });
  }
  return [...accounts.values()];
};

/** An ad set that uses one or more of the audiences a delete removes. */
export type AdSetAtRisk = {
  adSet: MetaAdSet;
  /** Each audience it uses, as "<account name> · <audience label>". */
  audiences: string[];
};

/** Every ad set using an audience in the grouped preview, once each, in first-seen order. */
export const adSetsAtRisk = (accounts: DeletePreviewAccount[]): AdSetAtRisk[] => {
  const byId = new Map<string, AdSetAtRisk>();
  for (const account of accounts)
    for (const audience of account.audiences)
      for (const adSet of audience.adSets) {
        const entry = byId.get(adSet.id) ?? { adSet, audiences: [] };
        entry.audiences.push(`${account.adAccountName ?? account.adAccountId} · ${audience.label}`);
        byId.set(adSet.id, entry);
      }
  return [...byId.values()];
};
